import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { PlatformEnum, PlatformService } from '@panamax/app-state';
import { OrderItem } from '@usf/ngrx-order';
import { ProductPropertiesEnum } from '@usf/product-types/Product-Summary';
import { ItemTypes } from 'src/app/lists/shared/list-detail-management/model/list-detail-management-view.model';
import { UsfProductCardModeEnum } from 'src/app/shared/constants/usf-product-card-mode.enum';
import { AlternativeProductCardService } from 'src/app/shared/services/product-card/alternative-product-card.service';
import { TagProduct } from '../../../shared-product/components/usf-product-tag-list/models/tag-product.model';
import { QuantityUpdate } from '../../../shared/models/quantity-update';
import { ImageVariantEnum } from '../../models/image-variant.enum';
import {
  ProductAlternative,
  ProductAlternativeTypeEnum,
} from '../../models/product-alternative';
import { ProductAlternativeSearchService } from '../../services/product-alternative-search.service';
import { ProductDetailTagsService } from '../../services/product-detail-tags.service';
import {
  getAlternativeThumbnail,
  getFirstImageURL,
} from '../../utils/product-images.util';
import { SUBSTITUTE_TYPE } from '@product-detail/constants/constants';
import { take } from 'rxjs/operators';
import { ToggleNewSearchService } from '@shared/services/toggle-new-search/toggle-new-search.service';
import { SEARCH_PAGES_ENUM } from '@shared/constants/search-pages.enum';
import { Product } from '@shared/models/product.model';
import { ProductAnalyticsService } from '@usf/ngrx-product';

@Component({
  selector: 'app-product-alternative',
  templateUrl: './product-alternative.component.html',
  styleUrls: ['./product-alternative.component.scss'],
})
export class ProductAlternativeComponent implements OnInit, OnChanges {
  private readonly MAX_MOBILE_TAGS = 2;

  @Input() productAlternative: ProductAlternative;
  @Input() originalProduct: Product;
  @Input() orderItem: OrderItem;
  @Input() selectedProductNumber: number;
  @Input() pricingDisabled: boolean;
  @Input() isDisable = false;
  @Input() isGuestUser: boolean;
  @Output() quantityUpdateEmitter = new EventEmitter<QuantityUpdate>();

  propertiesEnum = ProductPropertiesEnum;
  displayTags: TagProduct[];
  thumbnailImageURL: string;
  currentPlatform: PlatformEnum;
  isReplacement = false;
  isSubstitute = false;
  maxTags: number;
  subType = SUBSTITUTE_TYPE;

  constructor(
    private productDetailTagsService: ProductDetailTagsService,
    private productAlternativeSearchService: ProductAlternativeSearchService,
    private alternativeProductCardService: AlternativeProductCardService,
    readonly platformService: PlatformService,
    private readonly toggleNewSearchService: ToggleNewSearchService,
    private productAnalyticsService: ProductAnalyticsService,
  ) {}

  ngOnInit(): void {
    this.currentPlatform = this.platformService.getPlatformType();
    this.updateType();
    this.updateTags();
    this.thumbnailImageURL = getFirstImageURL(
      this.productAlternative?.product?.summary,
      ImageVariantEnum.Thumbnail,
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.productAlternative?.currentValue) {
      const {
        product: { pricing, summary },
      } = this.productAlternative;
      this.updateType();
      this.updateTags();
      this.thumbnailImageURL = getFirstImageURL(
        this.productAlternative?.product?.summary,
        ImageVariantEnum.Thumbnail,
      );
    }
  }

  changeQuantityHandler(quantityUpdate: QuantityUpdate) {
    this.quantityUpdateEmitter.emit(quantityUpdate);
  }

  searchReplacement(isSubstitute) {
    this.toggleNewSearchService
      .isNewSearchEnabled()
      .pipe(take(1))
      .subscribe(newSearchEnabled => {
        if (newSearchEnabled) {
          this.productAlternativeSearchService.newSearchReplacement(
            this.originalProduct?.summary,
            isSubstitute,
          );
        } else {
          this.productAlternativeSearchService.searchReplacement(
            this.originalProduct?.summary,
            UsfProductCardModeEnum.replacementSearch,
            false,
            this.productAlternative,
            SEARCH_PAGES_ENUM.pdp,
          );
        }
      });
  }

  getAlternativeThumbnail = (thumbnailImageURL: string): string =>
    getAlternativeThumbnail(
      this.productAlternative?.product?.summary,
      thumbnailImageURL,
    );

  private updateType(): void {
    this.isSubstitute =
      this.productAlternative.type === ProductAlternativeTypeEnum.substitute;
    this.isReplacement =
      this.productAlternative.type === ProductAlternativeTypeEnum.replacement;
  }

  hideActionButtons(): boolean {
    return (
      (this.isSubstitute || this.isReplacement) &&
      this.productAlternative.product.allowSearchForSubs
    );
  }

  openProductCardOptions(event: Event) {
    this.alternativeProductCardService.openAlternativeCardEllipsis({
      event,
      product: this.productAlternative.product,
      mode: UsfProductCardModeEnum.replacementSearch,
      pricingDisabled: false,
      isSelected: false,
      itemType: ItemTypes.product,
      list: null,
      isTouch: this.platformService.isTouch.value,
    });
  }

  private updateTags(): void {
    const CLICKABLE = true;
    this.displayTags = this.productDetailTagsService.getDisplayTags(
      this.productAlternative.product.summary,
      this.productAlternative.product.inventory,
      this.productAlternative.product.contract,
      this.productAlternative.product.customerPill,
      null,
      this.productAlternative.product.trackingAttributes,
      this.productAlternative.product.resultRank,
      CLICKABLE,
      this.isGuestUser,
    );
    if (this.currentPlatform === PlatformEnum.mobile) {
      this.maxTags = this.MAX_MOBILE_TAGS;
    } else {
      this.maxTags = this.displayTags?.length ?? 0;
    }
  }

  trackProductImageClick(product: Product) {
    this.productAnalyticsService.trackProductImageClick(
      product?.summary?.divisionNumber,
      product?.productNumber,
      product?.trackingAttributes,
      product?.resultRank,
    );
  }
}
